// The weeks ahead beside the lineup (B-316, DIRECTION U21): Plan ahead's
// grid (D10, B-252), one position under the next — each player's opponent by
// week, a bye shaded, and under each position who can play against how many
// it starts, short in the warning color. A week's head opens that week's
// lineup, on the same screen. Nothing on it says whom to add.

import type { PlanView } from '@fsp/domain';
import { usePlan } from '../../data/hooks';
import { useAtLeast } from '../../data/detail';
import { useLeagueSource } from '../../data/leagueSource';
import { useTier } from '../../shell/useTier';
import { EmptyState } from '../../kit';
import { FromLeague } from '../shared/FromLeague';
import type { PlayerCardData } from '../shared/PlayerCard';
import { PLAN_WEEKS, PlanGroupTable, PlanKey } from '../PlanScreen';
import '../../styles/screens/plan.css';

export function PlanPanel({
  teamId,
  onOpen,
}: {
  teamId: string;
  onOpen: (card: PlayerCardData) => void;
}) {
  const source = useLeagueSource();
  const snapshot = usePlan(source === 'api' ? teamId : null, PLAN_WEEKS);
  if (source === 'sim') {
    return (
      <EmptyState>
        Plan ahead reads a real league’s schedule. The tour plays one week of games, so there is
        nothing ahead to plan.
      </EmptyState>
    );
  }
  return (
    <FromLeague snapshot={snapshot}>
      {(view) => <PlanPanelBody view={view} onOpen={onOpen} />}
    </FromLeague>
  );
}

/** The first `n` weeks of a plan, every row and count with them. */
function trimWeeks(view: PlanView, n: number): PlanView {
  if (view.weeks.length <= n) return view;
  return {
    ...view,
    weeks: view.weeks.slice(0, n),
    groups: view.groups.map((g) => ({
      ...g,
      rows: g.rows.map((r) => ({ ...r, cells: r.cells.slice(0, n) })),
      able: g.able.slice(0, n),
    })),
  };
}

function PlanPanelBody({
  view,
  onOpen,
}: {
  view: PlanView;
  onOpen: (card: PlayerCardData) => void;
}) {
  const tints = useAtLeast('standard');
  // THREE WEEKS IN A TABLET'S PANEL: about 280 px held a name and four weeks
  // only by cutting "@WAS" (walked at 834). This week and the two after it are
  // the bye a manager is planning around; a desktop's wider panel has all
  // four. One read either way: the weeks are trimmed here.
  const tier = useTier();
  const shown = trimWeeks(view, tier === 'tablet' ? 3 : PLAN_WEEKS);
  if (shown.weeks.length === 0) {
    return <EmptyState>The season has no weeks left to plan.</EmptyState>;
  }
  return (
    <div className="fsp-side-list fsp-side-plan">
      <PlanKey tints={tints} />
      {shown.groups
        .filter((g) => g.rows.length > 0 || g.starting > 0)
        .map((g) => (
          <PlanGroupTable key={g.position} group={g} view={shown} onOpen={onOpen} />
        ))}
    </div>
  );
}
